// src/pages/Winners.js
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";

// Toggle to reveal the Aastaloos section when its draw has happened.
// It is built out in code already, but kept hidden for now.
const SHOW_AASTALOOS = false;

const WinnerRow = ({ winner, highlight = false }) => (
  <div className={`winner-row ${highlight ? "winner-row--main" : ""}`}>
    <span className="winner-ticket">{winner.ticket}</span>
    <span className="winner-prize">{winner.prize}</span>
    <span className="winner-location">{winner.location}</span>
  </div>
);

const WinnersSection = ({ title, accent, data }) => {
  if (!data) return null;

  return (
    <section className="winners-section" aria-label={title}>
      <h2 className={`winners-section-title ${accent}`}>{title}</h2>

      {(data.period || data.drawDate) && (
        <p className="winners-meta">
          {data.period && (
            <span>
              Osalesid {data.period} ostetud mängukombinatsioonid.
            </span>
          )}
          {data.drawDate && (
            <span>
              {" "}
              <strong>Loosimine: {data.drawDate}</strong>
            </span>
          )}
        </p>
      )}

      <div className="winners-table" role="table">
        <div className="winner-row winner-row--head" role="row">
          <span className="winner-ticket">Pileti number</span>
          <span className="winner-prize">Võit</span>
          <span className="winner-location">Müügikoht / kuupäev</span>
        </div>

        {data.mainWinner && <WinnerRow winner={data.mainWinner} highlight />}

        {(data.winners || []).map((w, i) => (
          <WinnerRow key={`${w.ticket}-${i}`} winner={w} />
        ))}
      </div>
    </section>
  );
};

const Winners = () => {
  const { setLanguageFromURL } = useTranslation();
  const { lang } = useParams();

  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    if (lang) {
      setLanguageFromURL(lang);
    }
  }, [lang, setLanguageFromURL]);

  // Winners are fetched fresh on every page load / visit and must never be
  // served from cache, so we disable the HTTP cache and bust the URL.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setStatus("loading");
      try {
        const base = process.env.PUBLIC_URL || "";
        const res = await fetch(`${base}/winners.json?t=${Date.now()}`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setStatus("ready");
        }
      } catch (err) {
        if (!cancelled) {
          setStatus("error");
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="winners-page" role="main" aria-labelledby="winners-title">
      <div className="container">
        <header className="page-header">
          <h1 id="winners-title">Vikinglotto lisavõitude võitjad</h1>
        </header>

        {status === "loading" && (
          <p className="winners-status">Laadin võitjate nimekirja…</p>
        )}

        {status === "error" && (
          <p className="winners-status">
            Võitjate nimekirja laadimine ebaõnnestus. Palun proovi hiljem uuesti.
          </p>
        )}

        {status === "ready" && (
          <>
            <WinnersSection
              title="Jaaniloos"
              accent="accent-jaaniloos"
              data={data?.jaaniloos}
            />

            {SHOW_AASTALOOS && (
              <WinnersSection
                title="Aastaloos"
                accent="accent-aastaloos"
                data={data?.aastaloos}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Winners;
